export const PROGRAMMATIC_SITE_ORIGIN = 'https://tresopilot.fr';

export const PROGRAMMATIC_PAGES = [
  {
    path: '/micro-entrepreneur/logiciel-tresorerie',
    title: 'Logiciel de trésorerie pour micro-entrepreneur — TrésoPilot',
    description:
      'Pilotez votre trésorerie de micro-entrepreneur sans tableur : CA encaissé, vrai net, provisions URSSAF/TVA/CFE, rappels et vision claire de ce que vous pouvez vous verser.',
    heroBadge: 'Guide micro-entrepreneur',
    heroTitle: 'Un logiciel de trésorerie pensé pour les micro-entrepreneurs',
    heroDescription:
      'Quand vous êtes en micro, le vrai problème n’est pas seulement de facturer. C’est de savoir ce qui vous reste vraiment après cotisations, impôts, TVA et échéances. Cette page vous montre comment structurer ce pilotage simplement.',
    summaryPoints: [
      'Suivi du CA réellement encaissé',
      'Estimation du vrai net disponible',
      'Rappels d’échéances URSSAF, TVA et CFE',
    ],
    painPoints: [
      'Vous voyez votre chiffre d’affaires, mais pas ce qui est réellement disponible après provisions.',
      'Les échéances sociales et fiscales arrivent souvent trop vite quand elles ne sont pas centralisées.',
      'Le tableur finit par devenir fragile dès que l’activité bouge ou que la TVA s’applique.',
    ],
    benefits: [
      'Visualiser en un coup d’œil votre trésorerie réellement mobilisable.',
      'Transformer les gros paiements ponctuels en provisions régulières plus faciles à absorber.',
      'Décider plus sereinement combien vous verser sans découvrir un trou de trésorerie ensuite.',
    ],
    featureTitle: 'Ce qu’un bon outil doit faire pour une micro-entreprise',
    featureBullets: [
      'Afficher le CA encaissé et non une vision floue du mois.',
      'Prévoir URSSAF, TVA, CFE et impôt selon votre profil.',
      'Donner un échéancier lisible au lieu de dates dispersées.',
      'Aider à lisser les charges avec des enveloppes ou provisions.',
      'Permettre des exports propres sans complexifier le quotidien.',
    ],
    sections: [
      {
        title: 'Pourquoi la trésorerie est le vrai sujet quand on est en micro',
        body:
          'Beaucoup de micro-entrepreneurs suivent surtout le chiffre d’affaires. Pourtant, ce qui protège votre activité, c’est la différence entre ce qui entre, ce qui doit être provisionné et ce que vous pouvez réellement garder. Sans cette lecture, vous risquez de confondre argent encaissé et argent disponible.',
      },
      {
        title: 'Comment TrésoPilot aide concrètement',
        body:
          'TrésoPilot centralise vos entrées, estime vos principales sorties à venir et affiche une vision plus exploitable de votre marge de manœuvre. L’objectif n’est pas d’ajouter un outil de plus, mais de réduire la charge mentale qui accompagne chaque déclaration.',
      },
      {
        title: 'À qui cette page est utile',
        body:
          'Cette approche convient particulièrement aux indépendants qui veulent sortir du pilotage “au feeling” : créateurs d’entreprise, activités de service, freelances, artisans et professions qui encaissent de façon irrégulière.',
      },
    ],
    faq: [
      {
        q: 'Un micro-entrepreneur a-t-il vraiment besoin d’un logiciel de trésorerie ?',
        a: 'Oui dès que vous voulez savoir ce que vous pouvez vous verser sans vous mettre en difficulté. Même avec une activité simple, les charges et échéances créent vite un décalage entre argent encaissé et argent réellement disponible.',
      },
      {
        q: 'Quelle différence avec un simple tableur ?',
        a: 'Le tableur peut suffire au départ, mais il repose sur votre discipline, vos formules et vos rappels manuels. Un outil dédié réduit le risque d’oubli et vous donne une lecture plus rapide au quotidien.',
      },
    ],
    relatedPaths: [
      '/micro-entrepreneur/calcul-remuneration',
      '/micro-entrepreneur/echeances-urssaf',
      '/micro-entrepreneur/seuils-tva',
    ],
  },
  {
    path: '/micro-entrepreneur/calcul-remuneration',
    title: 'Calculer sa rémunération en micro-entreprise — TrésoPilot',
    description:
      'Apprenez à estimer combien vous pouvez vous verser en micro-entreprise en tenant compte du CA encaissé, des provisions et des échéances à venir.',
    heroBadge: 'Guide rémunération',
    heroTitle: 'Comment estimer votre rémunération en micro-entreprise',
    heroDescription:
      'Le risque classique en micro n’est pas de ne pas facturer, mais de se payer trop tôt. Pour éviter ça, il faut distinguer ce qui entre, ce qui doit être mis de côté et ce que vous pouvez réellement prendre.',
    summaryPoints: [
      'Voir ce qui reste après provisions',
      'Éviter de se verser trop tôt',
      'Raisonner en trésorerie réelle',
    ],
    painPoints: [
      'Le compte bancaire donne l’illusion que tout l’argent disponible vous appartient déjà.',
      'Les cotisations et impôts arrivent après coup, quand la somme a parfois déjà été utilisée.',
      'Sans cadre, vous improvisez votre rémunération en fonction du solde du moment.',
    ],
    benefits: [
      'Définir un montant de rémunération plus prudent et plus stable.',
      'Limiter les à-coups entre les bons mois et les mois plus creux.',
      'Construire un pilotage compatible avec vos échéances réelles.',
    ],
    featureTitle: 'Les bons repères pour décider combien vous verser',
    featureBullets: [
      'Partir du chiffre d’affaires encaissé, pas du prévisionnel.',
      'Retirer les provisions sociales et fiscales avant toute décision.',
      'Tenir compte des dépenses déjà engagées ou à venir rapidement.',
      'Éviter les décisions uniquement basées sur le solde bancaire.',
      'Réévaluer régulièrement le montant si le rythme d’encaissement change.',
    ],
    sections: [
      {
        title: 'Le bon raisonnement',
        body:
          'La vraie question n’est pas “combien j’ai sur le compte ?”, mais “combien puis-je retirer sans fragiliser mes obligations à venir ?”. Cette logique évite de transformer une bonne période d’encaissement en tension de trésorerie quelques semaines plus tard.',
      },
      {
        title: 'Pourquoi un simulateur aide vraiment',
        body:
          'Un simulateur de rémunération utile sert à visualiser rapidement le reste après provisions, pas seulement à afficher un chiffre flatteur. C’est particulièrement utile quand l’activité est irrégulière ou saisonnière.',
      },
      {
        title: 'Ce que TrésoPilot apporte',
        body:
          'L’application relie vos encaissements, vos provisions et vos échéances pour produire une vision plus prudente de ce que vous pouvez vous verser. L’objectif est d’aider à décider, pas de vous pousser à retirer trop tôt.',
      },
    ],
    faq: [
      {
        q: 'Pourquoi le montant que je peux me verser change-t-il d’un mois à l’autre ?',
        a: 'Parce qu’il dépend de vos encaissements, de vos charges, de vos provisions et des échéances proches. Un bon mois de CA ne signifie pas automatiquement un bon mois de rémunération disponible.',
      },
      {
        q: 'Faut-il se fixer un salaire fixe en micro-entreprise ?',
        a: 'Cela peut aider pour la discipline, mais il faut que ce montant reste compatible avec votre trésorerie réelle. Le plus utile est souvent de définir une base prudente et de la réviser selon les encaissements et obligations.',
      },
    ],
    relatedPaths: [
      '/micro-entrepreneur/logiciel-tresorerie',
      '/micro-entrepreneur/echeances-urssaf',
      '/consultant-freelance/logiciel-tresorerie',
    ],
  },
  {
    path: '/micro-entrepreneur/echeances-urssaf',
    title: 'Suivre ses échéances URSSAF en micro-entreprise — TrésoPilot',
    description:
      'Centralisez vos échéances URSSAF en micro-entreprise et anticipez les montants à provisionner pour éviter les oublis et les mauvaises surprises.',
    heroBadge: 'Guide échéances',
    heroTitle: 'Mieux suivre vos échéances URSSAF quand vous êtes en micro',
    heroDescription:
      'Les échéances URSSAF ne posent pas seulement un problème de date. Elles posent un problème de préparation. Quand la date arrive sans provision suffisante, le stress monte très vite.',
    summaryPoints: [
      'Voir les dates à venir clairement',
      'Préparer les montants en avance',
      'Réduire la charge mentale administrative',
    ],
    painPoints: [
      'Les dates sont parfois connues, mais pas suffisamment anticipées.',
      'Un oubli d’échéance peut vite désorganiser la trésorerie du mois.',
      'Sans vue d’ensemble, on pilote souvent à la dernière minute.',
    ],
    benefits: [
      'Réduire le risque d’oubli ou de paiement dans l’urgence.',
      'Préparer progressivement le montant au lieu de le subir d’un coup.',
      'Relier calendrier et trésorerie pour prendre de meilleures décisions.',
    ],
    featureTitle: 'Ce qu’un bon suivi URSSAF doit montrer',
    featureBullets: [
      'La prochaine date importante de façon visible.',
      'Le montant estimatif à anticiper selon votre activité.',
      'Les provisions déjà mises de côté.',
      'Le lien entre l’échéance et vos encaissements récents.',
      'Des rappels utiles sans surcharger votre quotidien.',
    ],
    sections: [
      {
        title: 'Le vrai risque n’est pas la date, mais le décalage de préparation',
        body:
          'Une date connue mais mal préparée reste un problème. Quand vous n’avez pas constitué la réserve correspondante, l’échéance URSSAF vient rogner ce que vous pensiez pouvoir utiliser pour vous rémunérer ou pour financer d’autres besoins.',
      },
      {
        title: 'L’intérêt d’un échéancier centralisé',
        body:
          'Un échéancier utile doit rassembler les dates et les montants estimés au même endroit. Cela vous permet de ne pas raisonner “déclaration” d’un côté et “argent à mettre de côté” de l’autre.',
      },
      {
        title: 'L’approche TrésoPilot',
        body:
          'TrésoPilot vous aide à visualiser les échéances et à les relier à vos provisions. Cela rend le pilotage plus concret, surtout quand vous jonglez déjà avec la production, les clients et l’administratif.',
      },
    ],
    faq: [
      {
        q: 'Les rappels suffisent-ils pour être tranquille ?',
        a: 'Les rappels aident, mais ils ne remplacent pas la préparation financière. Le plus utile est de combiner la date, le montant estimé et la provision déjà constituée.',
      },
      {
        q: 'Pourquoi l’URSSAF crée-t-elle autant de stress chez les indépendants ?',
        a: 'Parce que beaucoup découvrent le montant au mauvais moment. Plus vous reliez la date à une logique de provision régulière, plus la pression diminue.',
      },
    ],
    relatedPaths: [
      '/micro-entrepreneur/logiciel-tresorerie',
      '/micro-entrepreneur/calcul-remuneration',
      '/micro-entrepreneur/seuils-tva',
    ],
  },
  {
    path: '/micro-entrepreneur/seuils-tva',
    title: 'Seuils de TVA en micro-entreprise — TrésoPilot',
    description:
      'Anticipez les seuils de TVA en micro-entreprise avec une vision claire de votre chiffre d’affaires encaissé et des alertes de suivi.',
    heroBadge: 'Guide TVA',
    heroTitle: 'Anticiper les seuils de TVA en micro-entreprise sans subir la bascule',
    heroDescription:
      'Le passage à la TVA peut fragiliser une activité quand il est vu trop tard. Le sujet n’est pas seulement réglementaire : il a un impact direct sur vos prix, vos encaissements et votre lecture de trésorerie.',
    summaryPoints: [
      'Suivre le CA encaissé utile pour la TVA',
      'Détecter les seuils avant d’être pris de court',
      'Préparer l’impact sur les prix et la trésorerie',
    ],
    painPoints: [
      'Le suivi du chiffre d’affaires n’est pas toujours structuré pour voir venir la bascule.',
      'La TVA change votre lecture de l’argent encaissé si vous ne distinguez pas clairement le TTC.',
      'Un changement fiscal mal anticipé peut brouiller vos marges et votre rémunération.',
    ],
    benefits: [
      'Voir venir la bascule suffisamment tôt pour décider sereinement.',
      'Adapter vos prix et vos messages clients avant l’urgence.',
      'Comprendre ce qui relève du TTC, de la TVA et du net réel.',
    ],
    featureTitle: 'Les indicateurs utiles à surveiller',
    featureBullets: [
      'Le CA encaissé cumulé sur la bonne période.',
      'La proximité des seuils avec un niveau d’alerte clair.',
      'L’impact de la TVA sur la trésorerie disponible.',
      'La différence entre argent encaissé et argent réellement conservable.',
      'Une vision lisible pour éviter les décisions dans la précipitation.',
    ],
    sections: [
      {
        title: 'Pourquoi le sujet TVA dépasse la simple conformité',
        body:
          'Quand la TVA entre dans le jeu, une partie de l’argent encaissé ne doit plus être interprétée comme du revenu disponible. Sans pilotage adapté, vous pouvez croire que votre activité accélère alors qu’une partie de la somme doit déjà être isolée.',
      },
      {
        title: 'Le lien entre seuils et prix',
        body:
          'Surveiller les seuils, ce n’est pas seulement vérifier un chiffre. C’est aussi préparer vos offres, vos devis et votre communication pour absorber le changement de cadre sans déséquilibrer votre rentabilité.',
      },
      {
        title: 'Ce que TrésoPilot peut simplifier',
        body:
          'L’application aide à suivre le chiffre d’affaires, à matérialiser les alertes et à remettre la TVA dans une logique de trésorerie concrète. Cela permet de prendre de l’avance au lieu de réagir après coup.',
      },
    ],
    faq: [
      {
        q: 'Pourquoi la TVA complique-t-elle autant le pilotage ?',
        a: 'Parce qu’elle change le sens d’une partie des encaissements. Sans distinction claire entre TTC, TVA et net utile, on surestime vite ce qui reste réellement disponible.',
      },
      {
        q: 'Peut-on attendre le dernier moment pour se préparer ?',
        a: 'C’est rarement une bonne idée. Plus vous anticipez, plus vous avez de marge pour ajuster vos prix, vos documents et votre organisation sans stress.',
      },
    ],
    relatedPaths: [
      '/micro-entrepreneur/logiciel-tresorerie',
      '/micro-entrepreneur/echeances-urssaf',
      '/artisan/logiciel-tresorerie',
    ],
  },
  {
    path: '/artisan/logiciel-tresorerie',
    title: 'Logiciel de trésorerie pour artisan — TrésoPilot',
    description:
      'Suivez vos encaissements, provisions et échéances avec un logiciel de trésorerie pensé pour les artisans qui doivent piloter à la fois chantier, clients et administratif.',
    heroBadge: 'Guide artisan',
    heroTitle: 'Un logiciel de trésorerie utile quand on est artisan',
    heroDescription:
      'Un artisan gère rarement une activité linéaire : acomptes, achats, décalages de paiement, périodes plus chargées, administratif en retard. Le pilotage de trésorerie doit donc rester simple, visuel et actionnable.',
    summaryPoints: [
      'Vision claire entre encaissements et obligations',
      'Pilotage plus simple malgré un rythme irrégulier',
      'Moins de charge mentale sur l’administratif',
    ],
    painPoints: [
      'Les encaissements ne tombent pas toujours au moment où les dépenses avancent déjà.',
      'Le terrain prend le dessus et l’administratif se repousse facilement.',
      'Sans vue rapide, il devient difficile de savoir ce qu’on peut engager ou se verser.',
    ],
    benefits: [
      'Mieux absorber les décalages entre chantiers, devis, acomptes et règlements.',
      'Garder une lecture simple même quand les journées sont déjà prises par l’opérationnel.',
      'Réduire le risque de surprises sur les échéances.',
    ],
    featureTitle: 'Les attentes concrètes d’un artisan',
    featureBullets: [
      'Un tableau de bord lisible en quelques secondes.',
      'Des rappels utiles sans jargon inutile.',
      'Une logique de provisions simple à suivre.',
      'La possibilité de produire ou exporter des documents propres.',
      'Un outil pensé pour l’action, pas pour alourdir le quotidien.',
    ],
    sections: [
      {
        title: 'Le terrain ne laisse pas beaucoup de place au pilotage complexe',
        body:
          'Quand la production, les rendez-vous et les urgences client occupent la journée, le logiciel de gestion doit aider à décider vite. Un bon outil pour artisan doit réduire la friction, pas exiger une logique de comptable.',
      },
      {
        title: 'La trésorerie artisanale est souvent une question de rythme',
        body:
          'L’enjeu est moins de faire des calculs compliqués que d’absorber les écarts entre achats, encaissements et obligations. Une bonne lecture de trésorerie permet de traverser ces décalages avec plus de marge.',
      },
      {
        title: 'Pourquoi TrésoPilot peut convenir',
        body:
          'TrésoPilot met l’accent sur les encaissements, les provisions et les échéances, avec une interface pensée pour aller à l’essentiel. C’est particulièrement utile quand vous voulez un cockpit simple plutôt qu’un outil lourd.',
      },
    ],
    faq: [
      {
        q: 'Un artisan a-t-il besoin d’un logiciel très complet dès le départ ?',
        a: 'Pas forcément. Le plus important est d’avoir une lecture fiable de la trésorerie, des provisions et des échéances. Un outil trop lourd peut décourager l’usage régulier.',
      },
      {
        q: 'Quel est le principal bénéfice pour un artisan ?',
        a: 'Gagner en visibilité malgré un rythme opérationnel soutenu. Quand le cockpit est clair, il devient plus facile d’anticiper et de décider rapidement.',
      },
    ],
    relatedPaths: [
      '/micro-entrepreneur/logiciel-tresorerie',
      '/micro-entrepreneur/seuils-tva',
      '/alternative/indy',
    ],
  },
  {
    path: '/consultant-freelance/logiciel-tresorerie',
    title: 'Logiciel de trésorerie pour consultant freelance — TrésoPilot',
    description:
      'Pilotez votre trésorerie de consultant freelance : encaissements, provisions, rémunération et échéances au même endroit.',
    heroBadge: 'Guide freelance',
    heroTitle: 'Un cockpit de trésorerie pour consultant freelance',
    heroDescription:
      'Le consultant freelance peut donner l’impression d’avoir une activité simple. Pourtant, entre encaissements décalés, missions irrégulières et besoin de se rémunérer correctement, la visibilité de trésorerie reste décisive.',
    summaryPoints: [
      'Lire le vrai disponible après provisions',
      'Mieux piloter entre deux missions',
      'Relier activité et rémunération',
    ],
    painPoints: [
      'Une mission signée ne signifie pas toujours un encaissement immédiat.',
      'Les périodes creuses demandent plus de prudence que le solde du compte ne le laisse croire.',
      'La rémunération devient floue quand l’activité alterne entre gros mois et creux.',
    ],
    benefits: [
      'Piloter avec plus de sérénité entre deux missions ou deux règlements.',
      'Éviter de confondre bon chiffre d’affaires ponctuel et niveau de sécurité durable.',
      'Rendre la rémunération plus cohérente avec la réalité des encaissements.',
    ],
    featureTitle: 'Les repères utiles pour un freelance',
    featureBullets: [
      'Une lecture claire des encaissements réellement entrés.',
      'Des provisions cohérentes avec le statut et les obligations.',
      'Une aide à la rémunération et à la projection de trésorerie.',
      'Des rappels sans complexité inutile.',
      'Un espace simple pour centraliser l’essentiel.',
    ],
    sections: [
      {
        title: 'Le pilotage freelance ne se résume pas à facturer',
        body:
          'Même avec peu de clients, les décalages de règlement et les variations de charge de travail peuvent brouiller votre vision. Un bon logiciel de trésorerie vous aide à garder un cap quand les missions ne s’enchaînent pas de façon parfaitement régulière.',
      },
      {
        title: 'Pourquoi la rémunération reste un sujet central',
        body:
          'Pour un consultant freelance, la rémunération est souvent décidée de manière opportuniste. Pourtant, elle devrait être reliée à un niveau de trésorerie plus stable et à des provisions déjà sécurisées.',
      },
      {
        title: 'L’apport de TrésoPilot',
        body:
          'TrésoPilot vous aide à faire ce lien entre encaissements, provisions, alertes et rémunération. L’idée est d’offrir un cadre léger mais utile pour piloter sans tableur complexe.',
      },
    ],
    faq: [
      {
        q: 'Même avec peu de factures, un freelance a-t-il besoin d’un tel outil ?',
        a: 'Oui, parce que le sujet n’est pas seulement le volume de factures. C’est la capacité à savoir ce qui reste vraiment disponible et ce que vous pouvez vous verser sans stress.',
      },
      {
        q: 'Le principal intérêt pour un consultant freelance ?',
        a: 'Garder une vision claire entre deux règlements et ne pas surinterpréter un gros mois ponctuel comme une sécurité durable.',
      },
    ],
    relatedPaths: [
      '/micro-entrepreneur/calcul-remuneration',
      '/micro-entrepreneur/logiciel-tresorerie',
      '/alternative/abby',
    ],
  },
  {
    path: '/alternative/indy',
    title: 'Alternative à Indy pour micro-entrepreneur — TrésoPilot',
    description:
      'Vous cherchez une alternative à Indy orientée pilotage de trésorerie, échéances et vrai net ? Découvrez l’approche TrésoPilot pour micro-entrepreneurs.',
    heroBadge: 'Comparatif',
    heroTitle: 'Vous cherchez une alternative à Indy ?',
    heroDescription:
      'Certaines personnes ne cherchent pas seulement un outil administratif. Elles veulent surtout un cockpit simple pour savoir ce qu’elles peuvent garder, provisionner et se verser. C’est là que l’angle TrésoPilot peut être pertinent.',
    summaryPoints: [
      'Approche centrée sur la trésorerie du quotidien',
      'Lecture du vrai net et des provisions',
      'Pilotage simple pour micro-entrepreneurs',
    ],
    painPoints: [
      'Vous cherchez peut-être moins une solution de production comptable qu’un outil de visibilité au quotidien.',
      'Vous voulez centraliser échéances, provisions et rémunération dans une interface simple.',
      'Vous préférez une logique de cockpit opérationnel plutôt qu’un univers perçu comme plus comptable.',
    ],
    benefits: [
      'Comparer les approches selon votre besoin réel.',
      'Choisir un outil aligné avec votre manière de piloter.',
      'Avancer vers une vision plus concrète de ce qui reste disponible.',
    ],
    featureTitle: 'Quand TrésoPilot peut mieux convenir',
    featureBullets: [
      'Si votre priorité est la trésorerie du quotidien.',
      'Si vous voulez savoir combien provisionner et combien vous verser.',
      'Si vous cherchez une interface directe, orientée action.',
      'Si les rappels et échéances sont un point sensible pour vous.',
      'Si vous voulez réduire la charge mentale liée au pilotage.',
    ],
    sections: [
      {
        title: 'Comparer deux approches plutôt que deux logos',
        body:
          'Une comparaison utile ne consiste pas à dire qu’un outil est “meilleur” en tout. Elle consiste à vérifier quel angle répond le mieux à votre besoin. Si votre priorité est la visibilité de trésorerie, l’approche TrésoPilot peut être plus cohérente.',
      },
      {
        title: 'Pour qui cette alternative a du sens',
        body:
          'Elle a du sens pour les micro-entrepreneurs qui veulent surtout savoir quoi faire avec l’argent encaissé : combien garder, combien mettre de côté, combien se verser et quelles échéances arrivent.',
      },
      {
        title: 'Ce que met en avant TrésoPilot',
        body:
          'TrésoPilot insiste sur le cockpit de trésorerie, les enveloppes de provisions, les rappels d’échéances, les alertes TVA et la lecture du vrai net. Cet angle peut être particulièrement parlant si vous cherchez de la clarté avant tout.',
      },
    ],
    faq: [
      {
        q: 'Pourquoi chercher une alternative à Indy ?',
        a: 'Souvent parce que le besoin principal n’est pas le même. Certains utilisateurs veulent avant tout un pilotage très concret de la trésorerie, des provisions et de la rémunération.',
      },
      {
        q: 'TrésoPilot remplace-t-il un expert-comptable ?',
        a: 'Non. L’outil aide au pilotage et à l’anticipation, mais ne remplace pas un conseil comptable ou fiscal personnalisé.',
      },
    ],
    relatedPaths: [
      '/alternative/abby',
      '/micro-entrepreneur/logiciel-tresorerie',
      '/artisan/logiciel-tresorerie',
    ],
  },
  {
    path: '/alternative/abby',
    title: 'Alternative à Abby pour micro-entrepreneur — TrésoPilot',
    description:
      'Découvrez une alternative à Abby orientée trésorerie, vrai net, provisions et échéances pour micro-entrepreneurs.',
    heroBadge: 'Comparatif',
    heroTitle: 'Vous cherchez une alternative à Abby ?',
    heroDescription:
      'Si vous comparez des outils pour micro-entrepreneurs, la vraie question est souvent : voulez-vous surtout produire de l’administratif, ou mieux piloter votre argent encaissé et vos échéances ? TrésoPilot se positionne clairement sur ce second besoin.',
    summaryPoints: [
      'Cockpit orienté trésorerie et visibilité',
      'Provisions et échéances au cœur de l’expérience',
      'Pilotage simple pour décider quoi garder ou vous verser',
    ],
    painPoints: [
      'Vous cherchez une lecture concrète de la trésorerie, pas seulement un outil de gestion générique.',
      'Vous voulez voir les provisions et les échéances avant qu’elles ne deviennent un problème.',
      'Vous avez besoin d’un repère clair pour votre rémunération.',
    ],
    benefits: [
      'Choisir un outil selon l’usage réel dont vous avez besoin.',
      'Mieux relier encaissements, charges futures et décisions de rémunération.',
      'Réduire le brouillard entre argent encaissé et argent réellement disponible.',
    ],
    featureTitle: 'Pourquoi certains indépendants préfèrent cette approche',
    featureBullets: [
      'La trésorerie reste la page d’entrée du pilotage.',
      'Les échéances et provisions sont visibles rapidement.',
      'Le vrai net et la rémunération sont plus faciles à interpréter.',
      'L’outil reste simple pour un usage fréquent.',
      'La logique vise la sérénité du quotidien.',
    ],
    sections: [
      {
        title: 'Comparer les outils par usage',
        body:
          'Une comparaison intelligente consiste à partir de votre besoin central. Si vous cherchez surtout à réduire la charge mentale liée à la trésorerie, aux échéances et à votre rémunération, TrésoPilot met précisément ces sujets au premier plan.',
      },
      {
        title: 'Quand cette alternative devient pertinente',
        body:
          'Elle devient pertinente quand vous avez déjà compris que le problème n’est pas seulement d’émettre des documents, mais de piloter une activité avec une vision claire de ce qui reste réellement disponible.',
      },
      {
        title: 'Le positionnement TrésoPilot',
        body:
          'TrésoPilot propose un angle cockpit : suivi du CA encaissé, provisions, alertes et échéancier, avec une lecture plus directe du vrai net. Cela peut parler aux indépendants qui veulent de la clarté avant tout.',
      },
    ],
    faq: [
      {
        q: 'Pourquoi une alternative à Abby peut-elle intéresser un micro-entrepreneur ?',
        a: 'Parce qu’un micro-entrepreneur peut chercher avant tout un pilotage de trésorerie concret, plus qu’une solution centrée sur d’autres usages. Tout dépend de la priorité quotidienne.',
      },
      {
        q: 'Quel est l’intérêt de TrésoPilot dans ce cas ?',
        a: 'Aider à relier encaissements, provisions, échéances et rémunération dans une interface simple, pour mieux décider au quotidien.',
      },
    ],
    relatedPaths: [
      '/alternative/indy',
      '/consultant-freelance/logiciel-tresorerie',
      '/micro-entrepreneur/calcul-remuneration',
    ],
  },
];

export const PROGRAMMATIC_PAGE_PATHS = PROGRAMMATIC_PAGES.map((page) => page.path);
export const PROGRAMMATIC_PAGE_MAP = Object.fromEntries(
  PROGRAMMATIC_PAGES.map((page) => [page.path, page])
);

export function getProgrammaticPage(pathname = '/') {
  return PROGRAMMATIC_PAGE_MAP[String(pathname || '/').split('#')[0].split('?')[0]] || null;
}

export function buildProgrammaticSchemas(page) {
  if (!page?.path) return [];

  const canonical = `${PROGRAMMATIC_SITE_ORIGIN}${page.path}`;
  const organization = {
    '@type': 'Organization',
    name: 'TrésoPilot',
    url: PROGRAMMATIC_SITE_ORIGIN,
    logo: `${PROGRAMMATIC_SITE_ORIGIN}/android-chrome-512x512.png`,
  };

  const webPage = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: page.title,
    description: page.description,
    url: canonical,
    about: page.heroTitle,
    isPartOf: {
      '@type': 'WebSite',
      name: 'TrésoPilot',
      url: PROGRAMMATIC_SITE_ORIGIN,
    },
    publisher: organization,
  };

  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Accueil',
        item: `${PROGRAMMATIC_SITE_ORIGIN}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: page.heroTitle,
        item: canonical,
      },
    ],
  };

  const faq = Array.isArray(page.faq) && page.faq.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: page.faq.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.a,
          },
        })),
      }
    : null;

  return [webPage, breadcrumb, faq].filter(Boolean);
}
